// MCIP-26 — Job trends screen (design board surface 2).
// Persona question, stated per board rule: "is this job getting better or
// worse, and why?" Exactly FOUR graphics (hard cap — we are not building a
// Datadog): weekly pass rate with deploys + incident bands, failure recurrence
// heatmap, incidents first seen, UNKNOWN share. Deep dives stay in the linked
// Allure/Datadog pages; this screen answers its one question.
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import SharedTopBar from '../shared/TopBar';
import PillGroup from '../shared/PillGroup';
import WeeklyPassRate from './WeeklyPassRate';
import RecurrenceHeatmap from './RecurrenceHeatmap';
import IncidentBars from './IncidentBars';
import UnknownShare from './UnknownShare';
import ImpactPanel from './ImpactPanel';
import SuiteChangesPanel from './SuiteChangesPanel';

const WINDOWS = [4, 8, 12];

function paramWeeks() {
  const w = Number(new URLSearchParams(window.location.search).get('weeks'));
  return WINDOWS.includes(w) ? w : 8;
}

export default function TrendsScreen({
  jobs, jobId, onJob,
  regions, region, onRegion, environments, environment, onEnvironment,
  mode, onMode, topbarNav,
}) {
  const [weeks, setWeeks] = useState(paramWeeks);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [asOf, setAsOf] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const fetchSeq = useRef(0);

  // ?weeks= keeps the window deep-linkable, same rule as ?job=/?run=.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (weeks === 8) url.searchParams.delete('weeks');
    else url.searchParams.set('weeks', String(weeks));
    window.history.replaceState({}, '', url);
  }, [weeks]);

  useEffect(() => {
    if (!jobId) {
      // Empty slice: the previous job's charts must not keep masquerading as
      // this slice's data (same honesty rule as the pods screen).
      setAnalytics(null);
      setError(null);
      return;
    }
    const seq = ++fetchSeq.current;
    setError(null);
    setStale(true);
    api.analytics(jobId, { weeks })
      .then((a) => {
        if (seq !== fetchSeq.current) return;
        setAnalytics(a);
        setAsOf(new Date());
      })
      .catch((e) => {
        if (seq !== fetchSeq.current) return;
        setError(e.message);
      })
      .finally(() => {
        if (seq === fetchSeq.current) setStale(false);
      });
  }, [jobId, weeks, retryTick]);

  const quiet = analytics && analytics.totals.runs === 0;

  return (
    <div className="app">
      <SharedTopBar
        regions={regions} region={region} onRegion={onRegion}
        environments={environments} environment={environment} onEnvironment={onEnvironment}
        asOf={asOf} refreshing={stale} onRefresh={() => setRetryTick((t) => t + 1)}
        mode={mode} onMode={onMode}
        nav={topbarNav}
      >
        <label className="field">
          <span>Job</span>
          <select value={jobId} onChange={(e) => onJob(e.target.value)}>
            {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.job_id}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Window</span>
          <PillGroup
            label="Trend window"
            options={WINDOWS.map((w) => String(w))}
            value={String(weeks)}
            onChange={(v) => setWeeks(Number(v))}
            format={(v) => `${v}w`}
          />
        </label>
      </SharedTopBar>

      <main className={`page${stale ? ' is-stale' : ''}`}>
        <p className="context">
          Job trends · {jobId || 'no job in this slice'} · last {weeks} weeks · is this job getting better or worse, and why?
        </p>

        {error && (
          <p className="notice notice--error">
            Couldn't load analytics for {jobId}: {error}
            {' '}
            <button type="button" className="ghost-btn" onClick={() => setRetryTick((t) => t + 1)}>
              Retry
            </button>
          </p>
        )}

        {!jobId && (
          <section className="card empty">
            <h2 className="card__title">No jobs in this slice</h2>
            <p className="card__subtitle">
              {String(region || 'us').toUpperCase()}/{environment || 'all environments'} has no
              harvested jobs to trend — switch region/environment above.
            </p>
          </section>
        )}

        {jobId && !analytics && !error && <p className="notice">Loading trends…</p>}

        {analytics && quiet && (
          <section className="card empty">
            <h2 className="card__title">No runs in this window</h2>
            <p className="card__subtitle">
              {jobId} recorded no runs in the last {weeks} weeks — nothing to trend.
              Widen the window, or harvest history from the ops panel (/admin).
            </p>
          </section>
        )}

        {analytics && !quiet && (
          <>
            <WeeklyPassRate series={analytics.series} mode={mode} />
            <div className="grid grid--even">
              <RecurrenceHeatmap analytics={analytics} mode={mode} />
              <div className="grid__side">
                <IncidentBars series={analytics.series} mode={mode} />
                <UnknownShare series={analytics.series} mode={mode} />
              </div>
            </div>
            {/* Text lists, not a fifth chart — the graphics budget stays 4. */}
            <ImpactPanel weeks={weeks} jobId={jobId} />
            {/* MCIP-29 v2 — suite changes live HERE (moved off the job view):
                delta chips + new-case lifecycle, each case linking to its
                signal; the pod drill honors the active slice. */}
            <SuiteChangesPanel
              jobId={jobId}
              pod={jobs.find((j) => j.job_id === jobId)?.pod || null}
              region={region}
              environment={environment}
            />
          </>
        )}
      </main>
    </div>
  );
}
